'use client';

/**
 * One interval for every relative timestamp on the page.
 *
 * A feed renders 30+ cards, so a `setInterval` per card would be 30 timers
 * doing identical work on the same schedule. Subscribers share a single
 * interval that only exists while someone is listening: the first subscriber
 * starts it, the last one to leave clears it, so an unmounted feed leaves
 * nothing running.
 *
 * A minute is the shortest granularity `formatRecency` can express ("5m ago"),
 * so ticking faster could not change a label. Ticking is cheap regardless of
 * how many labels are stale: subscribers re-derive their own string, and React
 * bails out of the re-render when that string is unchanged.
 */
const TICK_MS = 60_000;

const subscribers = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;

const tick = (): void => {
  // Copy first: a subscriber may unsubscribe while being notified, and
  // mutating the live Set mid-iteration would skip its neighbour. Built with
  // `forEach` rather than a spread because the panel's tsconfig target is
  // below the one spreading a Set would need.
  const snapshot: Array<() => void> = [];
  subscribers.forEach((notify) => snapshot.push(notify));
  snapshot.forEach((notify) => notify());
};

/** Subscribe to the shared minute tick. Returns an unsubscribe function. */
export const subscribeToMinuteTick = (notify: () => void): (() => void) => {
  subscribers.add(notify);
  timer ??= setInterval(tick, TICK_MS);

  return () => {
    subscribers.delete(notify);
    if (subscribers.size === 0 && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };
};

/** Test seam: drops every subscriber and stops the interval. */
export const resetMinuteTick = (): void => {
  subscribers.clear();
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
};
